<template>
  <v-card>
    <v-card-title>{{ $t("register.verify_email_title") }}</v-card-title>
    <v-card-text>
      <div class="mb-4">
        <p class="text-body-1 mb-2">
          {{ $t("register.verify_email_message_1") }}
        </p>
        <p class="text-body-2 grey--text text--darken-1">
          {{ $t("register.verify_email_message_2") }}
        </p>
      </div>

      <v-form ref="form">
        <v-text-field
          outlined
          :label="$t('register.email_otp')"
          v-model="otp"
          clearable
          :rules="requiredRules"
        ></v-text-field>

        <v-btn
          block
          x-large
          color="primary"
          @click="startRegistration"
          :disabled="!email || resendAfterSeconds > 0"
        >
          {{ $t("register.resend_otp") }}
          {{ resendAfterSeconds > 0 ? `(${resendAfterSeconds}s)` : "" }}
        </v-btn>
      </v-form>
    </v-card-text>
    <v-card-actions class="text-right">
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('submit', { success: false })">
        {{ $t("cancel") }}
      </v-btn>
      <v-btn
        text
        color="primary"
        :disabled="!sessionId || !otp"
        @click="$refs.form.validate() && verifyEmail()"
      >
        {{ $t("verify") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { ref, onMounted } from "@vue/composition-api";
import { DateTime } from "luxon";

export default {
  name: "HkiaraceRegistrationEmailModal",
  props: {
    initialEmail: {
      type: String,
      default: "",
    },
    // Invoked with the latest resend_after ISO string whenever the
    // backend returns one, so the parent can keep its cooldown timer
    // synced with the server even after the modal closes.
    onUpdateResendAfter: {
      type: Function,
      default: null,
    },
  },
  setup(props, { root, emit }) {
    const requiredRules = ref([
      (value) => !!value || root.$t("register.required_fields"),
    ]);

    const email = ref(props.initialEmail || "");
    const emailRules = ref([
      (value) =>
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          value
        ) || root.$t("register.invalid_email"),
    ]);

    const otp = ref("");
    const sessionId = ref(null);
    const resendAfterSeconds = ref(0);
    const resendCountdownInterval = ref(null);
    const resendAfter = ref(null);

    const startCountdown = function (resendAfterTime) {
      // Clear any existing interval
      if (resendCountdownInterval.value) {
        clearInterval(resendCountdownInterval.value);
        resendCountdownInterval.value = null;
      }

      // Parse the resend_after ISO datetime from API response
      resendAfter.value = DateTime.fromISO(resendAfterTime);

      // Calculate initial seconds until resend is allowed
      resendAfterSeconds.value = Math.ceil(
        resendAfter.value.diffNow("seconds").seconds
      );

      // Start countdown interval
      resendCountdownInterval.value = setInterval(() => {
        resendAfterSeconds.value = Math.ceil(
          resendAfter.value.diffNow("seconds").seconds
        );
        if (resendAfterSeconds.value <= 0) {
          clearInterval(resendCountdownInterval.value);
          resendCountdownInterval.value = null;
          resendAfterSeconds.value = 0;
        }
      }, 1000);
    };

    const notifyParentResendAfter = function (iso) {
      if (iso && typeof props.onUpdateResendAfter === "function") {
        props.onUpdateResendAfter(iso);
      }
    };

    const startRegistration = function () {
      // If we already have a session ID, this is a resend request
      if (sessionId.value) {
        root.$callAPI(
          "hkiarace/registration/resend-otp/",
          {
            session_id: sessionId.value,
          },
          (response) => {
            if (response.body && response.body.success) {
              if (response.body.resend_after) {
                startCountdown(response.body.resend_after);
                notifyParentResendAfter(response.body.resend_after);
              }
            }
          }
        );
      } else {
        // Initial registration start
        root.$callAPI(
          "hkiarace/registration/start/",
          {
            email: email.value,
          },
          (response) => {
            if (response.body && response.body.success) {
              sessionId.value = response.body.session_id;
              if (response.body.resend_after) {
                startCountdown(response.body.resend_after);
                notifyParentResendAfter(response.body.resend_after);
              }
            }
          }
        );
      }
    };

    const verifyEmail = function () {
      if (!sessionId.value || !otp.value) return;

      root.$callAPI(
        "hkiarace/registration/verify-email/",
        {
          session_id: sessionId.value,
          otp: otp.value,
        },
        (response) => {
          if (response.body && response.body.success) {
            emit("submit", {
              success: true,
              sessionId: sessionId.value,
              email: email.value,
              message: response.body.message,
            });
          }
        }
      );
    };

    // Automatically send OTP when modal opens
    onMounted(() => {
      if (email.value) {
        startRegistration();
      }
    });

    return {
      requiredRules,
      email,
      emailRules,
      otp,
      sessionId,
      resendAfterSeconds,
      resendCountdownInterval,
      startRegistration,
      verifyEmail,
    };
  },
  beforeDestroy() {
    if (this.resendCountdownInterval) {
      clearInterval(this.resendCountdownInterval);
    }
  },
};
</script>

<style lang="less"></style>
